import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid
} from '@mui/material';

// Services
import { moveInventory } from '../services/inventoryService';
import { getWarehouses } from '../services/warehouseService';
import { getLocations } from '../services/locationService';

const MovePartDialog = ({ open, onClose, part, currentLocation, currentQuantity, onSuccess }) => {
  const [formData, setFormData] = useState({
    to_location_id: '',
    quantity: 1,
    notes: ''
  });

  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Load initial data
  useEffect(() => {
    if (open) {
      loadWarehouses();
      // Reset form when dialog opens
      setFormData({
        to_location_id: '',
        quantity: 1,
        notes: ''
      });
      setSelectedWarehouse('');
      setError(null);
    }
  }, [open]);

  const loadWarehouses = async () => {
    try {
      setLoading(true);
      const data = await getWarehouses();
      setWarehouses(data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading warehouses:', err);
      setError('Failed to load warehouses');
      setLoading(false);
    }
  };

  const loadLocations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getLocations({ warehouse_id: selectedWarehouse });
      // Filter out the current location
      const filteredLocations = data.filter(loc => loc.id !== currentLocation.id);
      setLocations(filteredLocations);
      setLoading(false);
    } catch (err) {
      console.error('Error loading locations:', err);
      setError('Failed to load locations');
      setLoading(false);
    }
  }, [selectedWarehouse, currentLocation.id]);

  // Load locations when warehouse is selected
  useEffect(() => {
    if (selectedWarehouse) {
      loadLocations();
    } else {
      setLocations([]);
      setFormData(prev => ({ ...prev, to_location_id: '' }));
    }
  }, [selectedWarehouse, loadLocations]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.to_location_id || !formData.quantity) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.quantity > currentQuantity) {
      setError(`Cannot move more than available quantity (${currentQuantity})`);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const moveData = {
        part_id: part.id,
        from_location_id: currentLocation.id,
        to_location_id: formData.to_location_id,
        quantity: parseInt(formData.quantity),
        notes: formData.notes || undefined
      };

      await moveInventory(moveData);

      if (onSuccess) {
        onSuccess();
      }

      handleClose();
    } catch (err) {
      console.error('Error moving inventory:', err);
      setError(err.message || 'Failed to move inventory');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      to_location_id: '',
      quantity: 1,
      notes: ''
    });
    setSelectedWarehouse('');
    setLocations([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Move Part: {part?.internal_part_number}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Current Location:</strong> {currentLocation.warehouse?.name} - Rack {currentLocation.rack}, Shelf {currentLocation.shelf}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Available Quantity:</strong> {currentQuantity}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Destination Warehouse</InputLabel>
                <Select
                  value={selectedWarehouse}
                  onChange={(e) => setSelectedWarehouse(e.target.value)}
                  label="Destination Warehouse"
                  disabled={loading}
                >
                  {warehouses.map((warehouse) => (
                    <MenuItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.name} ({warehouse.location})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Destination Location</InputLabel>
                <Select
                  value={formData.to_location_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, to_location_id: e.target.value }))}
                  label="Destination Location"
                  disabled={!selectedWarehouse || loading}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      Rack {location.rack} - Shelf {location.shelf} ({location.barcode})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Quantity to Move"
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                inputProps={{ min: 1, max: currentQuantity }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes about this move..."
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || loading || !formData.to_location_id || !formData.quantity}
        >
          {submitting ? <CircularProgress size={20} /> : 'Move Part'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MovePartDialog;
